import { useEffect, useId, useMemo, useRef, useState } from "react";

import styles from "./kit.module.css";

/** One thing the box may offer. `value` is what gets typed; `note` says what it means. */
export type Suggestion = {
  value: string;
  note?: string;
  /** A small tag on the right — where the word comes from (fact, column, stat). */
  kind?: string;
};

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/**
 * A text box that knows the words.
 *
 * The Add-a-stat form asked for fact keys — `balls_faced`, `finish_time` — in
 * plain inputs, so a person had to already know the sport's vocabulary to use
 * a form whose whole point is that they do not. This offers the words as they
 * type, with what each one means beside it, and still takes anything typed
 * when `free` is on (a filter value, say).
 *
 * Keyboard first: arrows move, Enter picks, Escape closes, Tab leaves the box
 * with whatever is in it. The list is filtered on the value and the note both,
 * so typing "time" finds `finish_time` and typing "seconds" finds it too.
 */
export function Combobox({
  value,
  onChange,
  suggestions,
  placeholder,
  free = true,
  id,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  suggestions: Suggestion[];
  placeholder?: string;
  /** Accept a value that is not in the list. Off, an unknown word is marked invalid. */
  free?: boolean;
  id?: string;
  disabled?: boolean;
}) {
  const own = useId();
  const listId = `${id ?? own}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  const shown = useMemo(() => {
    const needle = value.trim().toLowerCase();
    const hits = needle
      ? suggestions.filter(
          (s) =>
            s.value.toLowerCase().includes(needle) ||
            (s.note ?? "").toLowerCase().includes(needle),
        )
      : suggestions;
    // An exact match first, then the ones that start with it, then the rest —
    // so typing "runs" puts `runs` above `runs_conceded`.
    return [...hits]
      .sort((a, b) => rank(a, needle) - rank(b, needle))
      .slice(0, 12);
  }, [suggestions, value]);

  const known = suggestions.some((s) => s.value === value);
  const invalid = !free && value !== "" && !known;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open]);

  const pick = (s: Suggestion) => {
    onChange(s.value);
    setOpen(false);
  };

  /** Typing moves the highlight back to the top: the list underneath just changed. */
  const typed = (next: string) => {
    onChange(next);
    setActive(0);
    setOpen(true);
  };

  return (
    <div className={styles.combo} ref={box}>
      <input
        id={id}
        className={styles.control}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => typed(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(shown.length - 1, i + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === "Enter" && open && shown[active]) {
            e.preventDefault();
            pick(shown[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && shown.length > 0 ? (
        <ul className={styles.comboList} id={listId} role="listbox">
          {shown.map((s, i) => (
            <li key={s.value} role="option" aria-selected={i === active}>
              <button
                type="button"
                className={cx(styles.menuItem, styles.comboItem)}
                data-active={i === active}
                // mousedown, not click: the input blurs on mousedown and a
                // click handler would then fire on a closed list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className={styles.comboValue}>{s.value}</span>
                {s.note ? <span className={styles.comboNote}>{s.note}</span> : null}
                {s.kind ? <span className={styles.menuKind}>{s.kind}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open && shown.length === 0 && suggestions.length > 0 ? (
        <div className={cx(styles.comboList, styles.comboEmpty)}>
          {free ? "Nothing like that here — it will be taken as typed." : "Nothing like that here."}
        </div>
      ) : null}
    </div>
  );
}

function rank(s: Suggestion, needle: string): number {
  if (!needle) return 0;
  const v = s.value.toLowerCase();
  if (v === needle) return 0;
  if (v.startsWith(needle)) return 1;
  if (v.includes(needle)) return 2;
  return 3;
}
